import React, { Fragment, useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import ReactRouterPropTypes from 'react-router-prop-types';
import PropTypes from 'prop-types';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import CloseIcon from '@material-ui/icons/Close';
import Modal from '../../shared/components/Modal';
import {
  medicationListByMedicationFilter,
  removeMedicationMutation,
} from '../../shared/queries/Medication';
import {
  changeLoadingTrue,
  changeLoadingFalse,
} from '../../redux/actions/loadingActions';
import MatTable from '../../shared/components/material-table';
import Columns from './components/Columns';
import GraphQl from '../../GraphQL';
import { showNotificationAutoDestroy } from '../../shared/components/ShowNotification';
import { getFormattedDate } from '../../shared/utils/smartutils';
import {
  ACTION_NAME,
  MEDICATION_APPLICATION_STATUS,
  MEDICATION_STATUS_ENUM,
} from '../../shared/utils/constants';
import MedicationFilter from './components/MedicationFilter';
import { studentListByFilter } from '../../shared/queries/Student';

const Medication = (props) => {
  const [data, setData] = useState([]);
  const [modal, setModal] = useState(false);
  const [selectedDto, setSelectedDto] = useState({});
  const [filterIsOpen, setFilterIsOpen] = useState(false);
  const [students, setStudents] = useState([]);
  const [lastFilter, setLastFilter] = useState({
    id: 0,
    studentId: 0,
    filterByHasRecipe: false,
    hasRecipe: false,
    filterByStatus: true,
    status: MEDICATION_STATUS_ENUM.PENDING,
  });
  const { t, history, dispatch } = props;

  function showLoading() {
    dispatch(changeLoadingTrue());
  }

  function hideLoading() {
    dispatch(changeLoadingFalse());
  }

  const getMedicationStatus = (status) => {
    return t(`medication_application.status.${status}`);
  };

  const loadStudents = () => {
    const filter = {
      id: 0,
      classes: [],
      responsibles: [],
      menus: [],
      onlyActive: true,
    };
    showLoading();
    const query = studentListByFilter(filter);
    GraphQl(query, {})
      .then((res) => {
        if (res.studentListByFilter) {
          setStudents(res.studentListByFilter);
        }
      })
      .catch(() => {
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      })
      .finally(() => {
        hideLoading();
      });
  };

  const listAllMedications = (filter) => {
    const query = medicationListByMedicationFilter(filter);
    showLoading();
    GraphQl(query, {})
      .then((res) => {
        res.medicationListByMedicationFilter =
          res.medicationListByMedicationFilter.map((item) => ({
            ...item,
            formattedrequestDate: getFormattedDate(item.requestDate),
            formattedStatus: getMedicationStatus(item.status),
          }));
        setData(res.medicationListByMedicationFilter);
        hideLoading();
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  const toggleModal = () => {
    setModal(!modal);
  };

  const onFilterToggle = () => {
    setFilterIsOpen(!filterIsOpen);
  };

  const editItemClickHandler = (evt, rowData) => {
    setSelectedDto({
      rowData,
    });
    history.push(`/minime/medicacao/mnt/${rowData.id}`);
  };
  const addItemClickHandler = () => {
    history.push('/minime/medicacao/mnt/0');
  };

  const cancelItemClickHandler = (evt, rowData) => {
    setSelectedDto({
      ...rowData,
    });
    toggleModal();
  };

  const customActions = [
    {
      name: ACTION_NAME.CLOSE,
      icon: () => <CloseIcon />,
      toolbarOnSelect: true,
      tooltip: t('general.cancel'),
      onClick: cancelItemClickHandler,
    },
  ];

  const actionsDisabledByConditional = (propss) => {
    if (propss.actions && propss.actions.length) {
      const copyProps = { ...propss };
      copyProps.actions.find((a) => a.name === ACTION_NAME.CLOSE).hidden =
        propss.data.status === MEDICATION_APPLICATION_STATUS.CANCELED ||
        propss.data.status === MEDICATION_APPLICATION_STATUS.APPLIED;
    }
  };

  const normalizeFilter = (filter) => {
    const { requestFinalDate, requestInitialDate, requestDate } = filter;
    return {
      ...filter,
      requestInitialDate: requestInitialDate ? requestInitialDate.getTime() : 0,
      requestFinalDate: requestFinalDate ? requestFinalDate.getTime() : 0,
      requestDate: requestDate ? requestDate.getTime() : 0,
    };
  };

  const onFilterClickHandler = (filter) => {
    if (filterIsOpen) {
      onFilterToggle();
    }
    if (filter) {
      const normalizedFilter = normalizeFilter(filter);
      setLastFilter(normalizedFilter);
      listAllMedications(normalizedFilter);
    } else if (lastFilter) {
      listAllMedications(lastFilter);
    }
  };

  const cancelItem = () => {
    showLoading();
    const removeMutation = removeMedicationMutation(selectedDto.id);
    GraphQl(removeMutation, {})
      .then(() => {
        showNotificationAutoDestroy(
          'success',
          t('success.title'),
          t('success.successfully-canceled')
        );
      })
      .catch(() => {
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-remove')
        );
      })
      .finally(() => {
        toggleModal();
        onFilterClickHandler();
        hideLoading();
      });
  };

  const filterBuildFunction = () => (
    <MedicationFilter
      onSubmit={onFilterClickHandler}
      lastFilter={lastFilter}
      students={students}
    />
  );

  useEffect(() => {
    loadStudents();
    onFilterClickHandler();
  }, []);

  return (
    <Fragment>
      <Modal
        color="warning"
        title={t('notifications.attention')}
        btn="Warning"
        message={t('notifications.cancel_medication')}
        toggle={toggleModal}
        modal={modal}
        onConfirm={cancelItem}
      />
      <Container>
        <Row className="Table">
          <Col md={12} lg={12}>
            <Card>
              <CardBody className="material-table-card">
                <div className="material-table__wrap">
                  <MatTable
                    filterBuildFunction={filterBuildFunction}
                    isFiltering={filterIsOpen}
                    filterToggle={onFilterToggle}
                    data={data}
                    columns={Columns(t)}
                    pageSize={20}
                    deleteAction={toggleModal}
                    onRefreshClick={onFilterClickHandler}
                    onAddClick={addItemClickHandler}
                    onEditClick={editItemClickHandler}
                    title={t('mnt_medication.title')}
                    actionsDisabledByConditional={actionsDisabledByConditional}
                    customActions={customActions}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

Medication.propTypes = {
  t: PropTypes.func.isRequired,
  history: ReactRouterPropTypes.history.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default withTranslation('common')(
  connect((state) => ({
    loading: state.loading,
  }))(Medication)
);
